import { useRef, useState } from "react";
import { UploadSimple, TextT, Anchor, Brain, Stack } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Oct 16, 2026 (Randy) — the Silo Art face of the three tier cards.
 *
 * The library IS the watering hole: it is the one surface every creator
 * touches every session, and in the stock look it reads like a settings
 * panel. When Silo Art is on (stage 1 or 2) these three cards take over and
 * the tier becomes a place — T1 on the ridge at sunrise, T2 at the falls, T3
 * in the valley at dusk. Art off returns the original card, untouched.
 *
 * Randy's rules for this face, verbatim in intent:
 *   · weight badges, the Always-On/cost meter, the index-build prompt, the
 *     rights line and the paste char counter all stay behind on the stock look
 *   · the tooltip comes along, top-left corner
 *   · Vector/RAG only on Tier 1, showing whichever mode is ACTIVE
 *   · every button keeps working, Paste still opens a real text area
 *
 * The art is three static WebPs shipped with the app (~276 KB for all three,
 * lazy, cached) — the same scenes for every creator, so there is no
 * per-silo generation cost and nothing to store.
 */

const ART = {
  1: {
    src: "/art/tiers/t1.webp",
    icon: Anchor,
    name: "The Anchor",
    tagline: "Your core truth. Always on.",
    accent: "#E8A33D",
    ring: "rgba(232,163,61,0.55)",
    glow: "rgba(232,163,61,0.22)",
    wash: "rgba(232,163,61,0.12)",
    scrim: 0.9,
  },
  2: {
    src: "/art/tiers/t2.webp",
    icon: Brain,
    name: "The Logic Core",
    tagline: "Process. Reason. Connect.",
    accent: "#5BA3E8",
    ring: "rgba(91,163,232,0.55)",
    glow: "rgba(91,163,232,0.22)",
    wash: "rgba(91,163,232,0.14)",
    scrim: 0.9,
  },
  3: {
    src: "/art/tiers/t3.webp",
    icon: Stack,
    name: "The Library",
    tagline: "Depth. History. Everything.",
    accent: "#B28BE8",
    ring: "rgba(178,139,232,0.55)",
    glow: "rgba(178,139,232,0.22)",
    wash: "rgba(178,139,232,0.14)",
    // T3's scene fades into bright fog at the foot, so its scrim works harder
    // to keep the white copy legible.
    scrim: 0.96,
  },
};

export function TierArtCard({
  tier,
  meta,
  tooltip,
  usedLabel,
  capLabel,
  pct,
  coreMode,
  onModeChange,
  modeBusy,
  onUpload,
  onPaste,
  busy,
}) {
  const art = ART[tier];
  const Icon = art.icon;
  const fileRef = useRef(null);
  const [pasting, setPasting] = useState(false);
  const [pasteTitle, setPasteTitle] = useState("");
  const [pasteContent, setPasteContent] = useState("");

  const submitPaste = async () => {
    if (!pasteContent.trim()) return toast.error("Paste content first.");
    await onPaste({ title: pasteTitle || "Untitled passage", content: pasteContent });
    setPasteTitle(""); setPasteContent(""); setPasting(false);
  };

  return (
    <div
      className="siloart-encased relative overflow-hidden rounded-2xl flex flex-col min-h-[30rem] transition-transform duration-300 hover:-translate-y-0.5"
      style={{
        border: `1px solid ${art.ring}`,
        boxShadow: `0 0 0 1px rgba(255,255,255,0.04) inset, 0 18px 40px -24px ${art.glow}, 0 0 22px -6px ${art.glow}`,
      }}
      data-testid={`tier-art-card-${tier}`}
    >
      <img
        src={art.src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            `linear-gradient(to top, rgba(8,8,11,${art.scrim - 0.55}) 0%, rgba(8,8,11,0.12) 55%, rgba(8,8,11,0) 100%)`,
        }}
      />

      {/* Top rail — tooltip left, the live Tier 1 mode right. */}
      <div className="relative z-10 flex items-start justify-between p-3.5 gap-2">
        <span className="shrink-0 drop-shadow">{tooltip}</span>
        {tier === 1 && (
          <button
            type="button"
            onClick={() => onModeChange?.(coreMode ? "full_injection" : "core_library")}
            disabled={modeBusy}
            data-testid="tier1-mode-toggle"
            data-mode={coreMode ? "core_library" : "full_injection"}
            title={coreMode
              ? "Tier 1 is a searchable Core Library: only the files you pin Always On ride in every prompt, the rest are searched. Tap to switch to Full Injection."
              : "Tier 1 is Full Injection: every file is sent with every question, nothing is searched. Tap to switch to Core Library (searched, with Always On pins)."}
            className="shrink-0 flex items-center gap-2 pl-2.5 pr-1.5 py-1.5 rounded-full text-left transition-colors disabled:opacity-50"
            style={{
              background: "rgba(10,12,14,0.55)",
              border: `1px solid ${coreMode ? "rgba(91,232,155,0.45)" : "rgba(240,194,84,0.45)"}`,
              backdropFilter: "blur(8px)",
            }}
          >
            <span className="leading-tight">
              <span
                className="block text-[11px] font-medium"
                style={{ color: coreMode ? "#8BF0BB" : "#F3CE7A" }}
              >
                {coreMode ? "Vector/RAG" : "Full injection"}
              </span>
              <span className="block text-[9px] uppercase tracking-wider text-white/50">
                Choose injection
              </span>
            </span>
            <span
              className="w-3.5 h-3.5 rounded-full shrink-0"
              style={{
                background: coreMode ? "#5BE89B" : "#F0C254",
                boxShadow: `0 0 8px ${coreMode ? "rgba(91,232,155,0.8)" : "rgba(240,194,84,0.8)"}`,
              }}
            />
          </button>
        )}
      </div>

      {/* The scene breathes in the gap; everything else sits at the foot, on
          its own scrim so the copy stays legible no matter how tall the card
          grows when the paste pad opens. */}
      <div
        className="relative z-10 mt-auto px-4 pb-4 pt-12"
        style={{
          background:
            `linear-gradient(to top, rgba(7,7,10,${art.scrim}) 0%, rgba(7,7,10,${art.scrim - 0.08}) 55%,`
            + ` rgba(7,7,10,0.55) 82%, rgba(7,7,10,0) 100%)`,
        }}
      >
        <div className="flex items-end gap-2.5">
          <Icon size={30} weight="duotone" className="text-white/85 shrink-0 mb-1.5" />
          <div className="min-w-0">
            <h3 className="font-serif-display text-[27px] leading-none text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)] truncate">
              {art.name}
            </h3>
            <p className="text-[13px] text-white/70 mt-1.5 truncate">{art.tagline}</p>
          </div>
        </div>

        <div className="mt-3.5" data-testid={`tier-${tier}-usage`}>
          <div className="flex justify-between items-baseline text-[11.5px] text-white/75 mb-1.5">
            <span>{usedLabel} of {capLabel} used</span>
            <span className="tabular-nums" style={{ color: art.accent }}>{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${pct}%`,
                background: art.accent,
                boxShadow: `0 0 10px ${art.glow}`,
                transition: "width 240ms",
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5 mt-3.5">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid={`tier-${tier}-upload`}
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] text-white transition-colors disabled:opacity-50"
            style={{
              background: `linear-gradient(rgba(255,255,255,0.06), rgba(255,255,255,0.02)), ${art.wash}`,
              border: `1px solid ${art.ring}`,
            }}
          >
            <UploadSimple size={16} weight="duotone" /> Upload
          </button>
          <button
            onClick={() => setPasting((p) => !p)}
            disabled={busy}
            data-testid={`tier-${tier}-paste-toggle`}
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] text-white/90 bg-white/[0.07] border border-white/15 hover:bg-white/[0.12] transition-colors disabled:opacity-50"
          >
            <TextT size={16} weight="duotone" /> Paste
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.txt,.md"
            className="hidden"
            onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }}
            data-testid={`tier-${tier}-file-input`}
          />
        </div>

        {pasting && (
          <div className="mt-2.5 space-y-2" data-testid={`tier-${tier}-paste-panel`}>
            <input
              value={pasteTitle}
              onChange={(e) => setPasteTitle(e.target.value)}
              placeholder="Title (optional)"
              data-testid={`tier-${tier}-paste-title`}
              className="w-full bg-black/45 border border-white/15 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-white/35"
            />
            <textarea
              value={pasteContent}
              onChange={(e) => setPasteContent(e.target.value)}
              rows={5}
              data-testid={`tier-${tier}-paste-content`}
              className="w-full bg-black/45 border border-white/15 rounded-lg px-3 py-2 font-mono text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-white/35"
              placeholder={`Paste a passage destined for ${meta.label}…`}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setPasting(false); setPasteTitle(""); setPasteContent(""); }}
                data-testid={`tier-${tier}-paste-cancel`}
                className="text-xs px-3 py-1.5 text-white/60 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={submitPaste}
                disabled={busy || !pasteContent.trim()}
                data-testid={`tier-${tier}-paste-submit`}
                className="text-xs px-3.5 py-1.5 rounded-lg text-[#0B0B0E] font-medium disabled:opacity-50"
                style={{ background: art.accent }}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TierArtCard;
